import type { LoaderFunctionArgs } from "react-router"

import {
  fetchSessionValidity,
  getAuthenticatedUserQueryKey,
  getUser,
  isSessionValidQueryKey,
} from "@/contexts"
import type { User } from "@/models/user"
import { isUnauthorizedError, queryClient } from "@/utils/queryClient"
import {
  redirectToSignInFromRequest,
  resolveLoaderError,
} from "@/utils/route-loader-error"

interface AuthenticatedRouteLoaderValue {
  isSessionValid: boolean | undefined
  user: User | undefined
}

const resolveCachedQueryError = (
  queryKey: readonly unknown[],
  request: Request
): Response | undefined => {
  const queryState = queryClient.getQueryState(queryKey)

  if (
    queryState?.status !== "error" ||
    !isUnauthorizedError(queryState.error)
  ) {
    return undefined
  }

  return redirectToSignInFromRequest(request)
}

export const authenticatedRouteLoader = async ({
  request,
}: LoaderFunctionArgs): Promise<AuthenticatedRouteLoaderValue | Response> => {
  try {
    const cachedSessionError = resolveCachedQueryError(
      isSessionValidQueryKey,
      request
    )

    if (cachedSessionError) {
      return cachedSessionError
    }

    const isSessionValid: boolean | undefined = await queryClient.fetchQuery({
      queryKey: isSessionValidQueryKey,
      queryFn: fetchSessionValidity,
    })

    if (isSessionValid === false) {
      return redirectToSignInFromRequest(request)
    }

    const cachedUserError = resolveCachedQueryError(
      getAuthenticatedUserQueryKey,
      request
    )

    if (cachedUserError) {
      return cachedUserError
    }

    const user = await queryClient.fetchQuery({
      queryFn: getUser,
      queryKey: getAuthenticatedUserQueryKey,
    })

    return {
      isSessionValid,
      user,
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error(error)
    }

    console.error("Error preloading authenticated route data")

    return resolveLoaderError(error, request)
  }
}
